import * as React from "react";
import { useEffect, useState, type InputHTMLAttributes } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/design-system/primitives";
import { Icon } from "@/lib/Icon";
import { cn } from "@/lib/cn";

/**
 * Set-new-password landing for the recovery email sent by /login's
 * "Forgot password?" (supabase.auth.resetPasswordForEmail →
 * redirectTo /reset-password).
 *
 * The recovery link carries its token in the URL; the supabase client
 * (detectSessionInUrl) exchanges it for a session asynchronously, so we
 * wait for either an existing session or the PASSWORD_RECOVERY /
 * SIGNED_IN auth event before showing the form — a used/expired link
 * never produces one and times out to the invalid state. Only EXISTING
 * accounts can ever get here (the reset API cannot create users), so
 * this adds no signup surface.
 */

type Stage = "checking" | "ready" | "invalid";

export function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setStage("ready");
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      if (mounted && data.session) setStage("ready");
    });
    const timeout = setTimeout(() => {
      if (mounted) setStage((s) => (s === "checking" ? "invalid" : s));
    }, 5000);
    return () => {
      mounted = false;
      clearTimeout(timeout);
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      navigate("/app", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't update the password",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      {stage === "checking" ? (
        <p className="font-body text-15 text-[#7C7C7C]">
          Checking your reset link…
        </p>
      ) : stage === "invalid" ? (
        <div className="flex flex-col gap-25">
          <h1 className="type-headline-lg text-black">
            This link isn&apos;t valid
          </h1>
          <p className="font-body text-15 text-[#7C7C7C]">
            The password-reset link is invalid or has expired. Request a
            fresh one from the sign-in page.
          </p>
          <Link to="/login" className="footer-link self-start">
            Back to sign in
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-25">
          <h1 className="type-headline-lg text-black">Set a new password</h1>
          <p className="font-body text-15 text-[#7C7C7C]">
            Choose a password for your account. You can always keep using
            email sign-in links too.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-15">
            <Field
              type="password"
              placeholder="New password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Field
              type="password"
              placeholder="Confirm new password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />

            {error && (
              <div role="alert" className="font-body text-13 text-danger">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="filled"
              loading={busy}
              className="mt-5"
            >
              <span>Save password</span>
              <span className="flex items-center justify-center w-8 h-10">
                <Icon name="Arrow" viewBox="0 0 10 12" className="w-8 h-10" />
              </span>
            </Button>
          </form>
        </div>
      )}
    </Shell>
  );
}

/* Mirrors LoginScreen's shell so both auth surfaces read as one flow. */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-black">
      <header className="flex items-center justify-between border-b border-black/10 bg-[#ECECEC]">
        <div className="w-[5rem] h-[5rem] flex items-center justify-center bg-slate">
          <div className="w-full max-w-[3rem] text-black">
            <Icon name="Mark Line" viewBox="0 0 32 30" />
          </div>
        </div>
        <div className="font-mono text-12 uppercase text-black px-15">
          Patient Portal
        </div>
      </header>
      <main className="flex-1 flex flex-col px-15 md:px-30">
        <div className="w-full max-w-[44rem] mx-auto flex-1 flex flex-col justify-center pt-60 pb-60 md:pt-100 md:pb-100">
          {children}
        </div>
      </main>
    </div>
  );
}

function Field({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "bg-[#FAFAFA] border border-[#ECECEC] text-[#7C7C7C] placeholder:text-[#7C7C7C]",
        "rounded-none outline-none py-12 px-15 w-full min-w-0",
        "focus:outline-none",
        "font-body text-15",
        className,
      )}
    />
  );
}
